/**
 * Serializing a ComponentManifest back into its `{# @component … #}` comment —
 * the write half of the manifest grammar, used by the visual manifest editor.
 *
 * The comment stays the authoring syntax (and the only stored form inside the
 * fragment's content); a form edit regenerates it through here and splices it
 * over the old one. Round-trip contract, tested:
 * `parseManifest(replaceManifestComment(src, m))` reproduces `m`'s type,
 * label, params, slots and inline flag exactly. (Warnings are the parser's
 * report about *text*, so they are not part of the contract — a literal label
 * legitimately re-warns on every parse.)
 *
 * Formatting is canonical: aligned columns like the `core/*` seed files, one
 * directive per line. A hand-authored comment's own formatting is therefore
 * replaced on the first form edit — the panel only writes on a real edit, so
 * opening it never touches the content.
 */
import {
  ComponentManifest,
  ManifestParam,
  findManifestComment,
} from './manifest';

/**
 * An attribute value in the form the grammar can read back.
 *
 * Bare only when unambiguous: no whitespace (the token rule), not starting
 * with a quote (a bare `"x"` would reparse as a quoted `x`), not empty.
 * Quoted otherwise — which also covers the classic footgun where an unquoted
 * `{{ theme.colors.brand }}` default truncates to `{{` at parse time.
 */
function quoteAttr(value: string): string {
  if (value !== '' && !/\s/.test(value) && !/^["']/.test(value)) return value;
  if (!value.includes('"')) return `"${value}"`;
  if (!value.includes("'")) return `'${value}'`;
  // Both quote kinds present — the grammar cannot express that. Degrade
  // predictably rather than emit a comment that breaks the whole manifest.
  return `"${value.replace(/"/g, "'")}"`;
}

function paramAttrs(param: ManifestParam): string {
  const parts: string[] = [];
  if (param.labelKey) parts.push(`label=${quoteAttr(param.labelKey)}`);
  if (param.options !== undefined) {
    parts.push(`options=${quoteAttr(param.options.join('|'))}`);
  }
  if (param.default !== undefined) {
    parts.push(`default=${quoteAttr(param.default)}`);
  }
  if (param.required) parts.push('required');
  return parts.join(' ');
}

/**
 * The manifest as a canonical `{# … #}` comment; `''` when it declares nothing
 * at all (no type, label, params, slots or inline — there is no comment to
 * write, and the splice below then removes any existing one).
 */
export function serializeManifest(manifest: ComponentManifest): string {
  const lines: string[] = [];

  if (manifest.type) lines.push(`@component ${manifest.type}`);
  if (manifest.labelKey) lines.push(`@label ${quoteAttr(manifest.labelKey)}`);

  // Aligned columns, like the core seed files: names padded so kinds line up,
  // kinds padded so attributes line up. Parse-neutral (whitespace collapses).
  const nameWidth = Math.max(
    0,
    ...manifest.params.map((param) => param.name.length + 1),
  );
  const kindWidth = Math.max(
    0,
    ...manifest.params.map((param) => param.kind.length),
  );
  for (const param of manifest.params) {
    const attrs = paramAttrs(param);
    const head = `@param ${`${param.name}:`.padEnd(nameWidth)} ${param.kind.padEnd(attrs ? kindWidth : 0)}`;
    lines.push(attrs ? `${head} ${attrs}` : head.trimEnd());
  }

  for (const slot of manifest.slots) {
    lines.push(
      slot.labelKey
        ? `@slot ${slot.name}: label=${quoteAttr(slot.labelKey)}`
        : `@slot ${slot.name}:`,
    );
  }

  if (manifest.inline) lines.push('@inline');

  if (lines.length === 0) return '';
  if (lines.length === 1) return `{# ${lines[0]} #}`;
  return `{# ${lines[0]}\n${lines
    .slice(1)
    .map((line) => `   ${line}`)
    .join('\n')}\n#}`;
}

/**
 * The source with its manifest comment replaced by `manifest`'s canonical
 * form — spliced in place using the same discovery rule as the parser, so the
 * rest of the content is untouched byte for byte. With no existing comment the
 * new one is prepended; a manifest that declares nothing removes the comment
 * (and the newline that separated it from the content).
 */
export function replaceManifestComment(
  source: string,
  manifest: ComponentManifest,
): string {
  const serialized = serializeManifest(manifest);
  const existing = findManifestComment(source);

  if (!existing) {
    if (serialized === '') return source;
    return source === '' ? serialized : `${serialized}\n${source}`;
  }

  const before = source.slice(0, existing.index);
  let after = source.slice(existing.index + existing.length);
  if (serialized === '' && after.startsWith('\n')) after = after.slice(1);

  return `${before}${serialized}${after}`;
}
